/************************************************************
 * Description:
 * Streamlined typing animation function that cycles through
 * an array of strings, typing them out with optional markup
 * handling, pausing, and backspacing. Various speeds and
 * behaviors can easily be configured via an options object.
 ************************************************************/
import i18next from "i18next";
import {remark} from "remark";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import stripMarkdown from "strip-markdown";
import {unified} from "unified";

import {
    AssistantAnswer,
    AssistantReasoningContent,
    GPTModel,
    SourceInfo,
    RelatedSourceInfo,
    Usage,
    Artifact,
    DirectSearchResult,
    isExternalSourceType,
} from "../../types";
import {resolveDibHref} from "../../utils/dibUrls";
import {CHART_UNAVAILABLE_DEFAULT, ChartNode, isChartNode} from "./components/stream-parser/blocks/ChartBlock";
import {intlLocaleForLanguage} from "./components/stream-parser/blocks/numberLocale";
import {unitPhrase} from "./components/stream-parser/blocks/unitPhrase";
import {FENCED_BLOCK} from "./components/stream-parser/fencedBlocks";
import {
    CompareNode,
    CompareRow,
    COMPARE_UNAVAILABLE_DEFAULT,
    isCompareNode,
} from "./components/stream-parser/blocks/CompareBlock";
import {buildStatsMatrix, parseResolvedInfoBlock, ResolvedInfoBlock, infoScopeLabel} from "./components/stream-parser/blocks/InfoBlock";
import {metricLabel} from "./components/stream-parser/blocks/metricLabel";
import {deduplicateEmailFences, resolveEmailFence, resolvedInlineEmailIds} from "./components/stream-parser/blocks/selectedEmail";
import {emailPlainText, readEmailPayload} from "./components/artifacts/emailPayload";
import {getArtifactTitle} from "./components/artifacts/title";
import {
    selectedResultsForClipboard,
    stripResourcesFences,
} from "./components/stream-parser/blocks/selectedResources";
import {
    normalizeMathDelimiters,
    outsideCode,
} from "./components/stream-parser/utils";

const escapeHtml = (s: string) =>
    s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/**
 * Renders an artifact into clipboard HTML. Returns "" for types with no copy representation.
 * Generalized per type so new artifact types can be added alongside search_results.
 */
const artifactToClipboardHtml = (artifact: Artifact): string => {
    if (artifact.type === "email_draft") {
        const payload = readEmailPayload(artifact.payload);
        if (!payload) return "";
        const paragraph = (text: string) => `<p>${escapeHtml(text).replace(/\n/g, "<br>")}</p>`;
        return `<section>${paragraph(`${i18next.t("artifact.emailDraft.subjectLabel")}: ${payload.subject}`)}${[payload.greeting, payload.body, payload.signOff].filter(Boolean).map(paragraph).join("")}</section>`;
    }
    if (artifact.type === "search_results") {
        const results =
            (artifact.payload?.results as DirectSearchResult[]) ?? [];
        if (results.length === 0) return "";
        const items = results
            .map((r) => {
                const desc = r.description ?? r.snippet ?? "";
                const descHtml = desc ? ` - ${escapeHtml(desc)}` : "";
                return `<li><a href="${escapeHtml(
                    r.url
                )}" target="_blank"><strong>${escapeHtml(
                    r.title
                )}</strong></a>${descHtml}</li>`;
            })
            .join("\n");
        return `<section><h3>${escapeHtml(
            getArtifactTitle(artifact)
        )}</h3><ul>${items}</ul></section>`;
    }
    return "";
};

/**
 * Renders an artifact into clipboard plain text. Returns "" for types with no copy representation.
 */
const artifactToClipboardText = (artifact: Artifact): string => {
    if (artifact.type === "email_draft") {
        const payload = readEmailPayload(artifact.payload);
        return payload ? `\n\n${emailPlainText(payload, i18next.t("artifact.emailDraft.subjectLabel"))}\n\n` : "";
    }
    if (artifact.type === "search_results") {
        const results =
            (artifact.payload?.results as DirectSearchResult[]) ?? [];
        if (results.length === 0) return "";
        const items = results
            .map((r) => {
                const desc = r.description ?? r.snippet ?? "";
                return `${r.title}${desc ? ` - ${desc}` : ""} (${r.url})`;
            })
            .join("\n");
        return `\n\n${getArtifactTitle(artifact)}\n${items}`;
    }
    return "";
};

/** Signature for a simple callback that receives updated text as it changes. */
export type TextChangedCallback = (
    updatedText: string,
    isFinished: boolean
) => void;

/** Options for controlling the type animation behavior. */
export interface TypeAnimationOptions {
    typingSpeed?: number;
    backspaceSpeed?: number;
    holdSpeed?: number;
    initialHoldSpeed?: number;
    waitSpeed?: number;
    loop?: boolean;
    backspaceOnFinish?: boolean;
    preserveMarkup?: boolean; // If false, markup is typed as plain text.
}

/**
 * Animate typing, holding, and backspacing over a single string.
 * @param text The string to animate.
 * @param onTextChanged Callback invoked whenever the animated text changes.
 * @param userOptions Optional configuration for speeds and loop behavior.
 * Loop is true by default. If false, the animation will stop after the first message and preserve the text.
 * @returns A promise that resolves when the animation is complete.
 */
export async function typeAnimatedText(
    text: string,
    onTextChanged: TextChangedCallback,
    userOptions?: TypeAnimationOptions
): Promise<void> {
    const {
        typingSpeed = 100,
        backspaceSpeed = 50,
        holdSpeed = 1000,
        waitSpeed = 2000,
        loop = true,
        preserveMarkup = true,
        backspaceOnFinish = true,
    } = userOptions || {};

    let animatedText = "";

    /**
     * Update the current text and call the user-supplied callback.
     */
    const updateText = (newText: string) => {
        animatedText = newText;
        onTextChanged(
            animatedText,
            !backspaceOnFinish && text.length === animatedText.length
        );
    };

    /**
     * Type out a string with optional markup handling.
     */
    const typeString = async (text: string) => {
        let tempMarkup = "";
        let insideMarkup = false;

        for (const char of text) {
            if (preserveMarkup && char === "<") {
                // If encountering a markup tag beginning
                insideMarkup = true;
                if (tempMarkup) {
                    // Append buffered text before opening the tag
                    updateText(animatedText + tempMarkup);
                    tempMarkup = "";
                }
                tempMarkup += char;
            } else if (preserveMarkup && insideMarkup && char === ">") {
                // Close the tag
                insideMarkup = false;
                tempMarkup += char;
                updateText(animatedText + tempMarkup);
                tempMarkup = "";
            } else if (preserveMarkup && insideMarkup) {
                // Continue inside the tag
                tempMarkup += char;
            } else {
                // Normal typing (or plain typing if preserveMarkup === false)
                updateText(animatedText + char);
                await sleep(typingSpeed);
            }
            // Small delay for re-render pacing (optional).
            await sleep(1);
        }

        // If something remains in tempMarkup after the loop ends, append it.
        if (tempMarkup) {
            updateText(animatedText + tempMarkup);
        }
    };

    /**
     * Backspace the entire animated text (including markup if preserveMarkup is enabled).
     */
    const backspaceString = async () => {
        if (!backspaceOnFinish) {
            return;
        }
        while (animatedText.length > 0) {
            // Handle markup backspacing
            if (preserveMarkup && animatedText.endsWith(">")) {
                const lastTagStart = animatedText.lastIndexOf("<");
                if (lastTagStart !== -1) {
                    animatedText = animatedText.slice(0, lastTagStart);
                    updateText(animatedText);
                    await sleep(backspaceSpeed);
                    continue;
                }
            }
            // Normal backspacing
            animatedText = animatedText.slice(0, -1);
            updateText(animatedText);
            await sleep(backspaceSpeed);
        }
    };

    /**
     * Main driver that loops through each message in sequence.
     */
    const runAnimation = async () => {
        do {
            await typeString(text);
            await sleep(holdSpeed);
            await backspaceString();
            await sleep(waitSpeed);
        } while (
            loop &&
            !(!backspaceOnFinish && animatedText.length === text.length)
        );
    };

    // Begin the animation
    await runAnimation();
}

/**
 * Animate typing, holding, and backspacing over a list of strings.
 * @param messages Array of strings to loop through.
 * @param onTextChanged Callback invoked whenever the animated text changes.
 * @param userOptions Optional configuration for speeds and loop behavior.
 */
export async function typeAnimatedTexts(
    messages: string[],
    onTextChanged: TextChangedCallback,
    userOptions?: TypeAnimationOptions
): Promise<void> {
    // Handle empty messages array
    if (!messages || messages.length === 0) {
        onTextChanged("", true); // Indicate finished state immediately
        return;
    }

    const {
        typingSpeed = 100,
        backspaceSpeed = 50,
        holdSpeed = 1000,
        initialHoldSpeed = 3000,
        waitSpeed = 2000,
        loop = true,
        backspaceOnFinish = true,
        preserveMarkup = true,
    } = userOptions || {};

    let animatedText = "";
    // let index = 0; // Removed, index logic handled differently below

    /**
     * Update the current text and call the user-supplied callback.
     */
    const updateText = (newText: string, isFinished = false) => {
        animatedText = newText;
        // Pass false for isFinished during animation, true only at the very end if not looping
        onTextChanged(animatedText, isFinished);
    };

    /**
     * Type out a string with optional markup handling.
     */
    const typeString = async (text: string) => {
        let tempMarkup = "";
        let insideMarkup = false;

        for (const char of text) {
            if (preserveMarkup && char === "<") {
                // If encountering a markup tag beginning
                insideMarkup = true;
                if (tempMarkup) {
                    // Append buffered text before opening the tag
                    updateText(animatedText + tempMarkup);
                    tempMarkup = "";
                }
                tempMarkup += char;
            } else if (preserveMarkup && insideMarkup && char === ">") {
                // Close the tag
                insideMarkup = false;
                tempMarkup += char;
                updateText(animatedText + tempMarkup);
                tempMarkup = "";
            } else if (preserveMarkup && insideMarkup) {
                // Continue inside the tag
                tempMarkup += char;
            } else {
                // Normal typing (or plain typing if preserveMarkup === false)
                updateText(animatedText + char);
                await sleep(typingSpeed);
            }
            // Small delay for re-render pacing (optional).
            await sleep(1);
        }

        // If something remains in tempMarkup after the loop ends, append it.
        if (tempMarkup) {
            updateText(animatedText + tempMarkup);
        }
    };

    /**
     * Backspace the entire animated text (including markup if preserveMarkup is enabled).
     */
    const backspaceString = async () => {
        while (animatedText.length > 0) {
            // Handle markup backspacing
            if (preserveMarkup && animatedText.endsWith(">")) {
                const lastTagStart = animatedText.lastIndexOf("<");
                if (lastTagStart !== -1) {
                    animatedText = animatedText.slice(0, lastTagStart);
                    updateText(animatedText);
                    await sleep(backspaceSpeed);
                    continue;
                }
            }
            // Normal backspacing
            animatedText = animatedText.slice(0, -1);
            updateText(animatedText);
            await sleep(backspaceSpeed);
        }
    };

    // --- Start of Modified Logic ---

    // 1. Initial Display & Wait
    updateText(messages[0]); // Show the first message instantly
    await sleep(initialHoldSpeed);

    // 2. Initial Backspace & Wait
    await backspaceString();
    await sleep(waitSpeed);

    // 3. Animation Loop
    let currentMessageIndex = 1; // Start loop processing from the second message

    // Check if there's only one message and no loop
    if (messages.length === 1 && !loop) {
        if (!backspaceOnFinish) {
            // Don't backspace, just mark as finished with the message still displayed
            updateText(messages[0], true);
        } else {
            updateText("", true); // Already backspaced, mark as finished
        }
        return; // Nothing more to do
    }

    // Main loop driver
    const runAnimationLoop = async () => {
        do {
            // Handle index wrap-around or termination
            if (currentMessageIndex >= messages.length) {
                if (loop) {
                    currentMessageIndex = 0; // Wrap around for looping
                } else {
                    updateText("", true); // Mark finished after last backspace/wait
                    break; // Exit loop if not looping
                }
            }

            const messageToType = messages[currentMessageIndex];

            // Type the message
            await typeString(messageToType);
            await sleep(holdSpeed);

            // Check if this is the last message and we're not looping
            const isLastMessage = currentMessageIndex === messages.length - 1;
            if (!loop && isLastMessage && !backspaceOnFinish) {
                // Keep the last message displayed and mark as finished
                updateText(messageToType, true);
                break;
            }

            // Backspace the message
            await backspaceString();
            await sleep(waitSpeed);

            // Prepare for next iteration
            currentMessageIndex++;

            // Added check: if only one message and looping, stay at index 0
            if (messages.length === 1 && loop) {
                currentMessageIndex = 0;
            }
        } while (true); // Loop is controlled by break statement or continues indefinitely if loop=true
    };

    // Begin the animation loop (after initial display/backspace)
    await runAnimationLoop();
    // --- End of Modified Logic ---
}

/**
 * Simple sleep utility.
 */
const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Increments citation numbers in markdown content to match 1-based source indices.
 * Converts [0] to [1], [1] to [2], etc.
 *
 * @param markdown The markdown content with citations
 * @returns Markdown with incremented citation numbers
 */
const incrementCitationNumbers = (markdown: string): string => {
    // First, handle escaped brackets \[(\d+)\] by removing the backslash
    let cleanedMarkdown = markdown.replace(/\\\[(\d+)\\\]/g, (match, num) => {
        const citationNumber = parseInt(num, 10);
        return `[${citationNumber + 1}]`;
    });
    
    // Then handle normal brackets [(\d+)]
    cleanedMarkdown = cleanedMarkdown.replace(/\[(\d+)\]/g, (match, num) => {
        const citationNumber = parseInt(num, 10);
        return `[${citationNumber + 1}]`;
    });
    
    return cleanedMarkdown;
};

// Dollar delimited math, after normalizeMathDelimiters has rewritten the bracket forms.
const LATEX_MATH = /\$\$([\s\S]*?)\$\$|\$([^$\n]+)\$/g;

/**
 * Rewrites the body of one formula as text. The list holds the commands that models write in
 * answers. Each other command keeps its name and loses its backslash, which is easier to
 * read than the raw command.
 */
const latexBodyToPlainText = (body: string): string =>
    body
        .replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, "$1/$2")
        .replace(/\\(?:text|mathbf|mathrm)\{([^{}]*)\}/g, "$1")
        .replace(/\\times/g, "×")
        .replace(/\\cdot/g, "·")
        .replace(/\\%/g, "%")
        .replace(/\\,/g, " ")
        .replace(/([\^_])\{([^{}]*)\}/g, "$1$2")
        // A group after a digit sets the spacing only, so the text keeps its content.
        // 480{,}000 is the LaTeX form of a thousands separator.
        .replace(/(\d)\{([^{}]*)\}/g, "$1$2")
        .replace(/\\\\/g, " ")
        .replace(/\\([a-zA-Z]+)/g, "$1")
        .replace(/\\(.)/g, "$1");

/**
 * Rewrites the LaTeX in an answer as plain text. KaTeX renders the formulas in the app, but
 * a document that receives the copied answer gets the source. "$420\,000$" must therefore
 * arrive as "420 000". Code keeps its backslashes and dollars, which are content there.
 */
export const latexToPlainText = (markdown: string): string =>
    outsideCode(normalizeMathDelimiters(markdown), (segment) =>
        segment.replace(LATEX_MATH, (_match, display?: string, inline?: string) =>
            latexBodyToPlainText(display ?? inline ?? "")
        )
    );

/** Attribution in resolved blocks also counts when no prose marker mentions the source. */
export const resolvedInfoSourceIndices = (content: string): number[] => {
    const indices = new Set<number>();
    for (const match of content.matchAll(FENCED_BLOCK)) {
        const block = parseResolvedInfoBlock(match[2]);
        if (block) [...(block.source ?? []), ...block.items.flatMap((item) => item.sources ?? [])].forEach((index) => indices.add(index));
    }
    return [...indices];
};

const unavailableInfoText = () => i18next.t("structuredBlock.unavailable", {defaultValue: "This answer could not be displayed."});
// A pasted link leaves the app, so a root-relative path needs our origin.
const clipboardHref = (url: string | null | undefined) => {
    const href = resolveDibHref(url);
    return escapeHtml(href.startsWith("/") ? `${window.location.origin}${href}` : href);
};

/** Copy resolved values directly; fields are text, never Markdown or executable HTML. */
const infoItemValue = (block: ResolvedInfoBlock, item: ResolvedInfoBlock["items"][number]) => {
    const value = block.type === "stats" ? item.display ?? "" : String(item.value ?? "");
    const context = [item.period, infoScopeLabel(item.scope), item.unit && !value.includes(item.unit) ? item.unit : undefined]
        .filter(Boolean).join("; ");
    const citations = [...new Set(item.sources ?? [])].map((source) => `[${source + 1}]`).join("");
    return `${value}${context ? ` (${context})` : ""}${item.qualification ? ` — ${item.qualification}` : ""}${citations ? ` ${citations}` : ""}`;
};

const infoContext = (block: ResolvedInfoBlock) => [block.period, infoScopeLabel(block.scope)].filter(Boolean).join("; ");
const infoSources = (block: ResolvedInfoBlock) => [...new Set(block.source ?? [])].map((source) => `[${source + 1}]`).join("");

const infoBlockToText = (block: ResolvedInfoBlock): string => {
    if (block.type === "stats" && block.dimensions?.length) {
        const dimensions = block.dimensions;
        const rows = buildStatsMatrix(block);
        const escapeCell = (value: string) => value.replace(/\\/g, "\\\\").replace(/\|/g, "\\|").replace(/\r?\n/g, "<br>");
        const headers = [i18next.t("compare.view.metric", {defaultValue: "Metric"}), ...dimensions.map(({label}) => label)];
        const matrix = [
            headers,
            headers.map(() => "---"),
            ...rows.map((row) => [
                `${row.displayLabel ?? row.label}${row.displayLabel && row.displayLabel !== row.label ? ` [${row.label}]` : ""}`,
                ...dimensions.map(({id}) => row.cells[id] ? infoItemValue(block, row.cells[id]!) : "—"),
            ]),
        ].map((row) => `| ${row.map((cell) => escapeCell(cell)).join(" | ")} |`).join("\n");
        return [block.title, [infoContext(block), infoSources(block)].filter(Boolean).join(" "), matrix, block.note]
            .filter(Boolean).join("\n\n");
    }
    return [
    block.title,
    [infoContext(block), infoSources(block)].filter(Boolean).join(" "),
    ...block.items.map((item) => `${item.displayLabel ?? item.label}${item.displayLabel && item.displayLabel !== item.label ? ` [${item.label}]` : ""}: ${infoItemValue(block, item)}`),
    block.note,
    ].filter(Boolean).join("\n");
};

const infoBlockToHtml = (block: ResolvedInfoBlock): string => {
    const paragraph = (text?: string) => text ? `<p>${escapeHtml(text).replace(/\n/g, "<br>")}</p>` : "";
    if (block.type === "stats" && block.dimensions?.length) {
        const rows = buildStatsMatrix(block).map((row) => `<tr><th scope="row" style="text-align:left;vertical-align:top;padding:4px 8px"${row.displayLabel && row.displayLabel !== row.label ? ` title="${escapeHtml(row.label)}"` : ""}>${escapeHtml(row.displayLabel ?? row.label)}</th>${block.dimensions!.map(({id}) => {
            const item = row.cells[id];
            return `<td style="padding:4px 8px">${item ? escapeHtml(infoItemValue(block, item)).replace(/\n/g, "<br>") : "—"}</td>`;
        }).join("")}</tr>`).join("");
        const metricLabel = i18next.t("compare.view.metric", {defaultValue: "Metric"});
        const headers = `<thead><tr><th scope="col" style="text-align:left;padding:4px 8px">${escapeHtml(metricLabel)}</th>${block.dimensions.map(({label}) => `<th scope="col" style="text-align:left;padding:4px 8px">${escapeHtml(label)}</th>`).join("")}</tr></thead>`;
        return `<section>${block.title ? `<h3>${escapeHtml(block.title)}</h3>` : ""}${paragraph([infoContext(block), infoSources(block)].filter(Boolean).join(" "))}<table style="border-collapse:collapse">${headers}<tbody>${rows}</tbody></table>${paragraph(block.note)}</section>`;
    }
    const rows = block.items.map((item) => `<tr><th scope="row" style="text-align:left;vertical-align:top;padding:4px 8px"${item.displayLabel && item.displayLabel !== item.label ? ` title="${escapeHtml(item.label)}"` : ""}>${escapeHtml(item.displayLabel ?? item.label)}</th><td style="padding:4px 8px">${escapeHtml(infoItemValue(block, item)).replace(/\n/g, "<br>")}</td></tr>`).join("");
    return `<section>${block.title ? `<h3>${escapeHtml(block.title)}</h3>` : ""}${paragraph([infoContext(block), infoSources(block)].filter(Boolean).join(" "))}<table style="border-collapse:collapse"><tbody>${rows}</tbody></table>${paragraph(block.note)}</section>`;
};

type ChartClipboardRow = {x: number | string; values: Array<number | undefined>};

/** Mirrors the chart's category merge and numeric ordering for its table view. */
const chartClipboardRows = (chart: ChartNode): ChartClipboardRow[] => {
    const rows = new Map<number | string, Array<number | undefined>>();
    chart.series.forEach((series, seriesIndex) => {
        for (const point of series.points) {
            const values = rows.get(point.x) ?? Array(chart.series.length).fill(undefined);
            values[seriesIndex] = point.y;
            rows.set(point.x, values);
        }
    });
    const result = [...rows].map(([x, values]) => ({x, values}));
    return result.every((row) => typeof row.x === "number")
        ? result.sort((a, b) => Number(a.x) - Number(b.x))
        : result;
};

const chartClipboardTitle = (chart: ChartNode): string =>
    chart.displayTitle ?? (chart.metric ? metricLabel(chart.metric, i18next.t) : chart.title);

const chartClipboardValue = (value: number | undefined, numberFormat: Intl.NumberFormat): string =>
    value === undefined ? "—" : numberFormat.format(value);

/** Copy the resolved chart points as a readable table. A chart that did not
 * resolve into explicit x/y points never reaches this formatter. */
const chartBlockToText = (chart: ChartNode): string => {
    const title = chartClipboardTitle(chart);
    const numberFormat = new Intl.NumberFormat(
        intlLocaleForLanguage(i18next.resolvedLanguage ?? i18next.language),
        {maximumFractionDigits: 20}
    );
    const axisLabel = i18next.t(typeof chart.series[0].points[0].x === "number" ? "chart.view.period" : "chart.view.category", {defaultValue: typeof chart.series[0].points[0].x === "number" ? "Period" : "Category"});
    const headers = [axisLabel, ...chart.series.map((series) => series.displayLabel && series.displayLabel !== series.label ? `${series.displayLabel} [${series.label}]` : series.displayLabel ?? series.label)];
    const rows = chartClipboardRows(chart).map((row) => [String(row.x), ...row.values.map((value) => chartClipboardValue(value, numberFormat))]);
    const table = [headers, ...rows].map((cells) => `| ${cells.map((cell) => cell.replace(/\|/g, "\\|").replace(/\n/g, " ")).join(" | ")} |`).join("\n");
    return [title, chart.subtitle, chart.unit && unitPhrase(chart.unit, i18next.t), table, chart.note].filter(Boolean).join("\n\n");
};

const chartBlockToHtml = (chart: ChartNode): string => {
    const title = chartClipboardTitle(chart);
    const numberFormat = new Intl.NumberFormat(
        intlLocaleForLanguage(i18next.resolvedLanguage ?? i18next.language),
        {maximumFractionDigits: 20}
    );
    const axisLabel = i18next.t(typeof chart.series[0].points[0].x === "number" ? "chart.view.period" : "chart.view.category", {defaultValue: typeof chart.series[0].points[0].x === "number" ? "Period" : "Category"});
    const headers = [axisLabel, ...chart.series.map((series) => series.displayLabel ?? series.label)];
    const head = `<tr>${headers.map((label, index) => `<th scope="col"${index > 0 && chart.series[index - 1].displayLabel && chart.series[index - 1].displayLabel !== chart.series[index - 1].label ? ` title="${escapeHtml(chart.series[index - 1].label)}"` : ""}>${escapeHtml(label)}</th>`).join("")}</tr>`;
    const body = chartClipboardRows(chart).map((row) =>
        `<tr><th scope="row">${escapeHtml(String(row.x))}</th>${row.values.map((value) => `<td>${escapeHtml(chartClipboardValue(value, numberFormat))}</td>`).join("")}</tr>`
    ).join("");
    const caption = [title && `<h3>${escapeHtml(title)}</h3>`, chart.subtitle && `<p>${escapeHtml(chart.subtitle)}</p>`, chart.unit && `<p>${escapeHtml(unitPhrase(chart.unit, i18next.t))}</p>`].filter(Boolean).join("");
    return `<section>${caption}<table style="border-collapse:collapse"><thead>${head}</thead><tbody>${body}</tbody></table>${chart.note ? `<p>${escapeHtml(chart.note)}</p>` : ""}</section>`;
};

const unavailableChartText = () => i18next.t("chart.unavailable", {defaultValue: CHART_UNAVAILABLE_DEFAULT});
const unavailableCompareText = () => i18next.t("compare.unavailable", {defaultValue: COMPARE_UNAVAILABLE_DEFAULT});

/** Internal company links are UI navigation only; copied text keeps the identity without its app-only URL. */
const companyLinksForClipboard = (content: string): string =>
    outsideCode(content, (segment) =>
        segment.replace(/\[([^\]]+)\]\(company:(\d{8,10})\)/g, "$1 (org.nr $2)")
    );

const chartSourceIndices = (content: string): number[] => {
    const indices = new Set<number>();
    for (const match of content.matchAll(FENCED_BLOCK)) {
        const lang = match[1].trim();
        const chart = isChartNode(match[2].trim(), {ignoreType: lang === "chart"});
        if (!chart) continue;
        const refs = Array.isArray(chart.source) ? chart.source : [chart.source];
        refs.forEach((ref) => { if (typeof ref === "number") indices.add(ref); });
    }
    return [...indices];
};

/** Keep block placement while letting remark sanitize prose and escaping resolved cells ourselves. */
const renderClipboardContent = (content: string, rich: boolean, artifacts?: Artifact[]): string => {
    content = companyLinksForClipboard(deduplicateEmailFences(content));
    const renderMarkdown = (markdown: string) => {
        const value = latexToPlainText(incrementCitationNumbers(markdown));
        return rich
            ? remark().use(remarkGfm).use(remarkHtml).processSync(value).toString()
            : remark().use(stripMarkdown).processSync(value).toString();
    };
    let result = "";
    let start = 0;
    for (const match of content.matchAll(FENCED_BLOCK)) {
        if (match[1].trim() === "email") {
            result += renderMarkdown(content.slice(start, match.index));
            const resolution = resolveEmailFence(match[2], artifacts, false);
            const unavailable = i18next.t("chat.email.unavailable", {defaultValue: "The email draft could not be displayed."});
            result += resolution.status === "ready"
                ? (rich ? artifactToClipboardHtml(resolution.artifact) : artifactToClipboardText(resolution.artifact))
                : rich ? `<p>${escapeHtml(unavailable)}</p>` : `${unavailable}\n\n`;
            start = match.index! + match[0].length;
            continue;
        }
        const language = match[1].trim();
        let parsed: unknown;
        try { parsed = JSON.parse(match[2]); } catch { /* Ordinary code stays code; known fences still get a safe fallback. */ }
        const parsedType = parsed && typeof parsed === "object" && "type" in parsed
            ? String((parsed as {type?: unknown}).type)
            : undefined;
        let chart: ChartNode | null = null;
        try { chart = isChartNode(match[2].trim(), {ignoreType: language === "chart"}); } catch { /* Invalid chart JSON uses the safe fallback below. */ }
        const chartFence = language === "chart" || parsedType === "chart" || !!chart;
        if (chartFence) {
            result += renderMarkdown(content.slice(start, match.index));
            result += chart
                ? (rich ? chartBlockToHtml(chart) : `${chartBlockToText(chart)}\n\n`)
                : rich ? `<p>${escapeHtml(unavailableChartText())}</p>` : `${unavailableChartText()}\n\n`;
            start = match.index! + match[0].length;
            continue;
        }
        const compare = isCompareNode(match[2].trim(), {ignoreType: language === "compare"});
        const compareFence = language === "compare" || parsedType === "compare" || !!compare;
        if (compareFence) {
            result += renderMarkdown(content.slice(start, match.index));
            result += compare
                ? rich
                    ? `<p>${escapeHtml(compareBlockToPlainText(compare)).replace(/\n/g, "<br>")}</p>`
                    : `${compareBlockToPlainText(compare)}\n\n`
                : rich ? `<p>${escapeHtml(unavailableCompareText())}</p>` : `${unavailableCompareText()}\n\n`;
            start = match.index! + match[0].length;
            continue;
        }
        const block = parseResolvedInfoBlock(parsed);
        const infoFence = /^(stats|details)$/.test(match[1].trim()) ||
            (parsed && typeof parsed === "object" && "type" in parsed && /^(stats|details)$/.test(String(parsed.type)));
        if (!block && !infoFence) continue;
        result += renderMarkdown(content.slice(start, match.index));
        if (block) result += rich ? infoBlockToHtml(block) : `${infoBlockToText(block)}\n\n`;
        else result += rich ? `<p>${escapeHtml(unavailableInfoText())}</p>` : `${unavailableInfoText()}\n\n`;
        start = match.index! + match[0].length;
    }
    const remaining = content.slice(start)
        .replace(/```email[^\n`]*(?:\r?\n[\s\S]*)?$/, () => i18next.t("chat.email.unavailable", {defaultValue: "The email draft could not be displayed."}))
        .replace(/```(?:stats|details)[^\n`]*(?:\r?\n[\s\S]*)?$/, () => unavailableInfoText())
        .replace(/```chart[^\n`]*(?:\r?\n[\s\S]*)?$/, () => unavailableChartText())
        .replace(/```compare[^\n`]*(?:\r?\n[\s\S]*)?$/, () => unavailableCompareText());
    return result + renderMarkdown(remaining);
};

/** Browsers without HTML clipboard support still receive the readable plain representation. */
export const writeAnswerToClipboard = async (text: string, html: string): Promise<void> => {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        try {
            await navigator.clipboard.write([new ClipboardItem({
                "text/plain": new Blob([text], {type: "text/plain"}),
                "text/html": new Blob([html], {type: "text/html"}),
            })]);
            return;
        } catch { /* Retry plain text when a browser rejects rich clipboard content. */ }
    }
    await navigator.clipboard.writeText(text);
};

/** Mirrors the compare visualization in plain text, keeping the baseline explicit. */
const compareItemsToText = (row: CompareRow): string => {
    const hasGapContract = row.items.some((item) => item.gapToLeader !== undefined);
    const items = row.items.map((item) => {
        const base = `${item.label} ${item.display ?? i18next.t("compare.noValue")}`;
        return hasGapContract && item.gapToLeader?.display
            ? `${base} (−${item.gapToLeader.display})`
            : base;
    });
    const baseline = hasGapContract
        ? `${i18next.t("compare.differenceFromHighest", {defaultValue: "Difference from highest"})}: `
        : "";
    return `${baseline}${items.join(", ")}`;
};

/** A row's own heading: its metric id resolved to a label when present,
 *  otherwise its label. Shared by the single-row and multi-row layouts
 *  below. */
const rowHeading = (row: CompareRow): string =>
    row.metric ? metricLabel(row.metric, i18next.t) : (row.label ?? "");

/**
 * Renders a compare block as one text line per row, e.g.
 * "Driftsmargin 2025: Difference from highest: DIBkunnskap AS 21,5 % (−10,7 pp), STICOS AS 32,2 %".
 * A single-row block reads the block title as its heading, unless the title
 * only repeats the row's own heading; several rows each keep their own
 * metric label under the title.
 */
export const compareBlockToPlainText = (compare: CompareNode): string => {
    const rows = compare.yearPages?.find((page) => page.year === compare.defaultYear)?.rows ?? compare.rows;
    if (rows.length === 1) {
        const row = rows[0];
        const resolvedHeading = rowHeading(row);
        const title = compare.displayTitle ?? compare.title;
        const heading = title && title !== resolvedHeading ? title : resolvedHeading;
        const body = compareItemsToText(row);
        if (!compare.subtitle && !compare.note) return `${heading}: ${body}`;
        return [heading, compare.subtitle, body, compare.note].filter(Boolean).join("\n");
    }
    const lines = rows.map((row) => `${rowHeading(row)}: ${compareItemsToText(row)}`);
    return [compare.displayTitle ?? compare.title, compare.subtitle, ...lines, compare.note].filter(Boolean).join("\n");
};

/**
 * Removes charts from text previews. Clipboard callers can preserve chart fences so the
 * clipboard renderer can replace resolved points with readable tables. Compare blocks keep
 * their existing plain-text representation.
 */
export const stripChartBlocks = (content: string, preserveChartsForClipboard = false): string =>
    content.replace(FENCED_BLOCK, (block, lang: string, body: string) => {
        const trimmedLang = lang.trim();
        const trimmedBody = body.trim();
        if (preserveChartsForClipboard && (trimmedLang === "chart" || isChartNode(trimmedBody))) {
            return block;
        }
        if (trimmedLang === "chart" || isChartNode(trimmedBody)) {
            return "";
        }
        const compare = isCompareNode(trimmedBody, {ignoreType: trimmedLang === "compare"});
        return compare ? compareBlockToPlainText(compare) : block;
    });

/**
 * Prepares an answer for the clipboard. Chart fences stay intact for table conversion by the
 * serializers. An external source is omitted unless prose or a structured block cites it.
 */
export const prepareAnswerForCopy = (
    content: string,
    sources: SourceInfo[]
): {content: string; sources: SourceInfo[]} => {
    const infoReferences = new Set([...resolvedInfoSourceIndices(content), ...chartSourceIndices(content)]);
    const external = new Set(
        sources.filter((s) => isExternalSourceType(s.type) && !infoReferences.has(s.index)).map((s) => s.index)
    );
    const allowedSources = new Set(sources.filter((s) => !external.has(s.index)).map((s) => s.index));
    const clipboardContent = stripChartBlocks(content, true).replace(FENCED_BLOCK, (fence, lang: string, body: string) => {
        let value: unknown;
        try { value = JSON.parse(body); } catch { return fence; }
        const block = parseResolvedInfoBlock(value);
        if (!block) return fence;
        const filtered = {
            ...block,
            source: block.source?.filter((index) => allowedSources.has(index)),
            items: block.items.map((item) => ({...item, sources: item.sources?.filter((index) => allowedSources.has(index))})),
        };
        return `\`\`\`${lang}\n${JSON.stringify(filtered)}\n\`\`\`\n`;
    });
    const text = external.size
        ? outsideCode(clipboardContent, (segment) =>
              segment.replace(/ ?\[(\d+)\]/g, (marker, n: string) =>
                  external.has(Number(n)) ? "" : marker
              )
          )
        : clipboardContent;
    return {
        content: text,
        sources: sources.filter((s) => !external.has(s.index)),
    };
};

/**
 * Returns a html string from the markdown content of the answer using remark-gfm. And creates a html list of the sources.
 *
 * @param answer AssistantAnswer
 * @returns html string
 */
export const prettyMessageForClipboard = (
    answer: AssistantAnswer,
    sources: SourceInfo[],
    relatedTools?: RelatedSourceInfo[],
    artifacts?: Artifact[]
) => {
    const html = renderClipboardContent(stripResourcesFences(answer.content), true, artifacts);
    const sourcesHtml = sources
        ?.map((source, index) => {
            return `<li><a href="${clipboardHref(source.url)}" target="_blank"><strong>${source.index + 1}. ${escapeHtml(source.name)}</strong></a></li>`;
        })
        .join("\n");
    
    // Filter relatedTools to only include those referenced in the message
    const filteredRelatedTools = answer?.content && relatedTools
        ? relatedTools.filter((tool) => answer.content.includes(tool.url))
        : [];
    
    const relatedToolsHtml = filteredRelatedTools.length > 0
        ? `<section><h3>${i18next.t(
              "references.relatedTools.title"
          )}</h3><ul>${filteredRelatedTools
              .map((tool) => {
                  return `<li><a href="${clipboardHref(
                      tool.url
                  )}" target="_blank"><strong>${tool.title}</strong> - ${
                      tool.description
                  }</a></li>`;
              })
              .join("\n")}</ul></section>`
        : "";
    
    const sourcesSectionHtml = sourcesHtml
        ? `<section><h3>${i18next.t(
              "references.title"
          )}</h3><ul>${sourcesHtml}</ul></section>`
        : "";

    const inlineEmailIds = resolvedInlineEmailIds(answer.content, artifacts);
    const clipboardArtifacts = (artifacts ?? []).filter((artifact) => !inlineEmailIds.has(artifact.id)).flatMap((artifact) =>
        selectedResultsForClipboard(artifact, answer.content)
    );
    const artifactsHtml = clipboardArtifacts
        .map(artifactToClipboardHtml)
        .join("");

    return `<div>${html}${sourcesSectionHtml}${relatedToolsHtml}${artifactsHtml}</div>`;
};

/**
 * Returns a plain text string from the markdown content of the answer using remark-parse and strip-markdown.
 *
 * @param answer AssistantAnswer
 * @returns plain text string
 */
export const plainTextForClipboard = (
    answer: AssistantAnswer,
    sources: SourceInfo[],
    relatedTools?: RelatedSourceInfo[],
    artifacts?: Artifact[]
) => {
    let text = renderClipboardContent(stripResourcesFences(answer.content), false, artifacts);
    
    // Clean up any remaining escaped brackets in the plain text
    text = text.replace(/\\\[(\d+)\\\]/g, '[$1]');
    text = text.replace(/\\\[/g, '[').replace(/\\\]/g, ']');
    // The caller passes the cited sources only, in display order, for both citation formats.
    const sourcesText = sources
        ?.map((source, index) => {
            return `${source.index + 1}. ${source.name}${isExternalSourceType(source.type) && source.url ? ` (${source.url})` : ""}`;
        })
        .join("\n");
    
    // Filter relatedTools to only include those referenced in the message
    const filteredRelatedTools = answer?.content && relatedTools
        ? relatedTools.filter((tool) => answer.content.includes(tool.url))
        : [];
    
    const relatedToolsText = filteredRelatedTools.length > 0
        ? `\n\n${i18next.t("references.relatedTools.title")}\n${filteredRelatedTools
              .map((tool) => `${tool.title} - ${tool.description}`)
              .join("\n")}`
        : "";
    
    const sourcesSectionText = sourcesText
        ? `\n\n${i18next.t("references.title")}\n${sourcesText}`
        : "";

    const inlineEmailIds = resolvedInlineEmailIds(answer.content, artifacts);
    const clipboardArtifacts = (artifacts ?? []).filter((artifact) => !inlineEmailIds.has(artifact.id)).flatMap((artifact) =>
        selectedResultsForClipboard(artifact, answer.content)
    );
    const artifactsText = clipboardArtifacts
        .map(artifactToClipboardText)
        .join("");

    return `${text}${sourcesSectionText}${relatedToolsText}${artifactsText}`;
};

// Pricing per 1M tokens
const priceTable: Record<GPTModel, {prompt: number; completion: number}> = {
    "gpt-4o": {
        prompt: 2.5,
        completion: 10.0,
    },
    "gpt-4o-mini": {
        prompt: 0.15,
        completion: 0.6,
    },
};

const nokRate = 11.11;

export const calculateCost = (usageDict?: Record<GPTModel, Usage>) => {
    if (!usageDict) {
        return {
            costUsd: "0.0000",
            costNok: "0.0000",
        };
    }

    const totalCost = Object.keys(usageDict).reduce((acc, curr) => {
        const price = priceTable[curr as GPTModel];
        if (!price || !usageDict[curr as GPTModel]) {
            return acc;
        }
        return (
            acc +
            (price.prompt * usageDict[curr as GPTModel].prompt_tokens) /
                1000000 +
            (price.completion * usageDict[curr as GPTModel].completion_tokens) /
                1000000
        );
    }, 0);

    return {
        costUsd: totalCost.toFixed(4),
        costNok: (totalCost * nokRate).toFixed(4),
    };
};

export function autoCompletePartialJson(json: string): string {
    let completedJson = json.trim();

    // Remove starting and ending markdown code block markers if present
    completedJson = completedJson.replace(/^```\s*|\s*```$/g, "");

    // Stack to track opening brackets/braces
    const stack: string[] = [];
    let inString = false;
    let isEscaped = false;

    // Analyze the string character by character
    for (let i = 0; i < completedJson.length; i++) {
        const char = completedJson[i];

        if (char === "\\") {
            isEscaped = !isEscaped;
            continue;
        }

        if (!isEscaped && char === '"') {
            inString = !inString;
        } else if (!inString && !isEscaped) {
            if (char === "{" || char === "[") {
                stack.push(char);
            } else if (char === "}") {
                if (stack[stack.length - 1] === "{") {
                    stack.pop();
                }
            } else if (char === "]") {
                if (stack[stack.length - 1] === "[") {
                    stack.pop();
                }
            }
        }

        isEscaped = false;
    }

    // Close unclosed strings
    if (inString) {
        completedJson += '"';
    }

    // Close any remaining open structures
    while (stack.length > 0) {
        const opener = stack.pop();
        if (opener === "{") {
            completedJson += "}";
        } else if (opener === "[") {
            completedJson += "]";
        }
    }

    return completedJson;
}

/**
 * For some reason the LLM restarts the JSON object after every stop_reason. So we take the last
 * JSON object and return that.
 * TODO: Fix this backend-side.
 */
export const llmBadJsonToGoodJson = (json: string): AssistantAnswer | null => {
    const completedJson = autoCompletePartialJson(json);
    try {
        return JSON.parse(completedJson) as AssistantAnswer;
        /* const split = completedJson.split('{"thinking');
        const last = `{"thinking${split[split.length - 1]}`;
        return JSON.parse(last) as AssistantAnswer; */
    } catch (error) {
        return null;
    }
};

export const openIntercom = () => {
    if ((window as any).Intercom) {
        (window as any).Intercom("show");
    }
};

export const closeIntercom = () => {
    if ((window as any).Intercom) {
        (window as any).Intercom("hide");
    }
};
